import { DIMENSOES_AGRUPAMENTO, type DimensaoAgrupamento } from '@baiocchi/shared'
import {
  Alerta,
  Botao,
  CabecalhoCartao,
  Campo,
  Cartao,
  CartaoIndicador,
  CorpoCartao,
  Esqueleto,
  EstadoVazio,
  Funil,
  Selecao,
  TabelaDados,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { conversoesApi } from '../../api/indicadores'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { FiltroPeriodo } from '../../componentes/filtro-periodo'
import { urlDoDrilldown } from '../../dominio/drilldown'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarNumero, formatarPercentual, formatarTempoRelativo } from '../../utilitarios/formatar'

const ROTULO_DIMENSAO: Record<DimensaoAgrupamento, string> = {
  pontoConversao: 'Ponto de conversão',
  equipe: 'Equipe',
  unidade: 'Unidade',
  responsavel: 'Responsável',
  motivo: 'Motivo de perda',
}

/**
 * Relatório de funil de conversão (`GET /conversoes/funil`).
 *
 * As etapas **não somam** o total: `Agendados` e `Com proposta` significam "existe
 * agendamento/proposta para este lead", não "está neste status agora" — um lead que
 * agendou, visitou e comprou conta nas três. É o comportamento correto para um funil
 * de passagem, e o rótulo diz "% do topo" justamente para não sugerir soma.
 */
export function PaginaFunil() {
  const { filtros, definir } = useFiltrosUrl({ agruparPor: 'pontoConversao' })
  const navegar = useNavigate()

  const consultaFiltros = {
    de: filtros.de,
    ate: filtros.ate,
    unidadeId: filtros.unidadeId,
    equipeId: filtros.equipeId,
    agruparPor: filtros.agruparPor,
  }

  const consulta = useQuery({
    queryKey: chaves.indicadores.funil(consultaFiltros),
    queryFn: ({ signal }) => conversoesApi.funil(consultaFiltros, signal),
    staleTime: 60_000,
  })

  const dados = consulta.data?.dados
  const meta = consulta.data?.meta

  return (
    <>
      <CabecalhoPagina
        titulo="Funil de conversão"
        descricao="Leads que chegaram no período e por onde passaram."
        acoes={
          meta ? (
            <span className="text-xs text-conteudo-fraco">
              Atualizado {formatarTempoRelativo(meta.atualizadoEm)}
            </span>
          ) : null
        }
      />

      <Cartao className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <FiltroPeriodo de={filtros.de} ate={filtros.ate} onAlterar={(periodo) => definir(periodo)} />
          <Campo rotulo="Agrupar por" className="w-full sm:w-56">
            {(atributos) => (
              <Selecao
                {...atributos}
                value={filtros.agruparPor ?? 'pontoConversao'}
                opcoes={DIMENSOES_AGRUPAMENTO.map((dimensao) => ({
                  valor: dimensao,
                  rotulo: ROTULO_DIMENSAO[dimensao],
                }))}
                onChange={(evento) => definir({ agruparPor: evento.target.value })}
              />
            )}
          </Campo>
        </div>
      </Cartao>

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <CartaoIndicador
              rotulo="Recebidos no período"
              valor={formatarNumero(
                dados?.etapas.find((etapa) => etapa.etapa === 'RECEBIDOS')?.total ?? 0,
              )}
              carregando={consulta.isPending}
            />
            <CartaoIndicador
              rotulo="Vendas realizadas"
              tom="sucesso"
              valor={formatarNumero(
                dados?.etapas.find((etapa) => etapa.etapa === 'VENDA_REALIZADA')?.total ?? 0,
              )}
              carregando={consulta.isPending}
            />
            <CartaoIndicador
              rotulo="Taxa de conversão"
              tom="info"
              valor={formatarPercentual(dados?.taxaConversao ?? 0)}
              descricao="Vendas sobre leads recebidos no período."
              carregando={consulta.isPending}
            />
          </div>

          <Cartao semPadding>
            <CabecalhoCartao
              titulo="Etapas do funil"
              descricao="Cada etapa conta os leads que passaram por ela — as etapas não somam o total."
            />
            <CorpoCartao>
              {consulta.isPending ? (
                <div className="space-y-3">
                  {Array.from({ length: 7 }, (_, indice) => (
                    <Esqueleto key={indice} className="h-8 w-full" />
                  ))}
                </div>
              ) : (
                <Funil
                  etapas={(dados?.etapas ?? []).map((etapa) => ({
                    rotulo: etapa.rotulo,
                    valor: etapa.total,
                    percentualDoTopo: etapa.percentualDoTopo,
                    onSelecionar: () => navegar(urlDoDrilldown(etapa.drilldown)),
                  }))}
                />
              )}
            </CorpoCartao>
          </Cartao>

          <Cartao semPadding>
            <CabecalhoCartao
              titulo={`Por ${ROTULO_DIMENSAO[(filtros.agruparPor as DimensaoAgrupamento) ?? 'pontoConversao'].toLowerCase()}`}
            />
            <TabelaDados
              colunas={[
                {
                  chave: 'rotulo',
                  cabecalho: ROTULO_DIMENSAO[
                    (filtros.agruparPor as DimensaoAgrupamento) ?? 'pontoConversao'
                  ],
                  celula: (linha) => <span className="font-medium">{linha.rotulo}</span>,
                },
                {
                  chave: 'recebidos',
                  cabecalho: 'Recebidos',
                  alinhamento: 'direita',
                  celula: (linha) => (
                    <span className="tabular-nums">{formatarNumero(linha.recebidos)}</span>
                  ),
                },
                {
                  chave: 'atendidos',
                  cabecalho: 'Atendidos',
                  alinhamento: 'direita',
                  ocultarAte: 'md',
                  celula: (linha) => (
                    <span className="tabular-nums">{formatarNumero(linha.atendidos)}</span>
                  ),
                },
                {
                  chave: 'vendas',
                  cabecalho: 'Vendas',
                  alinhamento: 'direita',
                  celula: (linha) => (
                    <span className="tabular-nums text-sucesso">{formatarNumero(linha.vendas)}</span>
                  ),
                },
                {
                  chave: 'perdas',
                  cabecalho: 'Perdas',
                  alinhamento: 'direita',
                  ocultarAte: 'md',
                  celula: (linha) => (
                    <span className="tabular-nums text-perigo">{formatarNumero(linha.perdas)}</span>
                  ),
                },
                {
                  chave: 'taxaConversao',
                  cabecalho: 'Conversão',
                  alinhamento: 'direita',
                  celula: (linha) => (
                    <span className="font-semibold tabular-nums">
                      {formatarPercentual(linha.taxaConversao)}
                    </span>
                  ),
                },
              ]}
              itens={dados?.porDimensao.linhas ?? []}
              chaveDe={(linha) => String(linha.id ?? linha.rotulo)}
              carregando={consulta.isPending}
              vazio={<EstadoVazio titulo="Sem dados no período" />}
            />
          </Cartao>

          <Alerta tom="info">
            A etapa <strong>Com proposta</strong> conta zero enquanto o módulo de propostas não
            existir (Fase 2) — a etapa está no cálculo, mas nada cria proposta hoje. A exportação do
            relatório depende do job de exportação com link expirável.
            <div className="mt-3">
              <Botao variante="contorno" tamanho="sm" onClick={() => navegar('/relatorios/perdas')}>
                Ver análise de perdas
              </Botao>
            </div>
          </Alerta>
        </div>
      )}
    </>
  )
}
